import { Box, Button, Option } from "@mui/joy";
import {
  AddCircleOutlineIcon,
  RemoveCircleOutlineIcon,
  DeleteOutlineIcon,
} from "../../../../styles/StyledMUI";
import DarkSelect from "../../../common/DarkSelect";
import DarkInput from "../../../common/DarkInput";
import NumericInput from "../../../common/NumericInput";
import type {
  EquipmentAttributeDefinition,
  AttributeOption,
} from "../../../../api/equipment/Equipment.typed";
import { getOperatorOptions } from "../../../../utils/prefilterHelpers";
import {
  EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL,
  equipmentAttributeUsesEnumPicklist,
  equipmentPicklistSelectDisplayValue,
  resolveEquipmentListFilterSentinel,
} from "../../../../utils/searchHelpers";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../../../styles/globalStyles";
import DarkTypography from "../../../common/DarkTypography";

export interface CriteriaItem {
  AttributeID: string;
  Operator: string;
  Value: string;
  Type?: string;
}

interface CriteriaBuilderProps {
  attributes: EquipmentAttributeDefinition[];
  criteria: CriteriaItem[];
  logic: "AND" | "OR";
  onChange: (next: { criteria: CriteriaItem[]; logic: "AND" | "OR" }) => void;
  readOnly?: boolean;
}

const parseRelativeToken = (token: string) => {
  const match = /^now(?:(\+|-)(\d+)m)?$/i.exec((token || "").trim());
  if (!match) return { relative: false, months: 3, sign: -1 as 1 | -1 } as const;
  const sign = (match[1] as ("+" | "-") | undefined) === "+" ? 1 : -1;
  const num = match[2] ? parseInt(match[2], 10) : 0;
  return { relative: true, months: num, sign } as const;
};

export default function CriteriaBuilder({
  attributes,
  criteria,
  logic,
  onChange,
  readOnly,
}: CriteriaBuilderProps) {
  const updateAt = (index: number, patch: Partial<CriteriaItem>) => {
    const next = [...criteria];
    next[index] = { ...next[index], ...patch };
    onChange({ criteria: next, logic });
  };

  const removeAt = (index: number) => {
    const next = criteria.filter((_, i) => i !== index);
    onChange({ criteria: next, logic });
  };

  const addNew = () => {
    const firstAttr = attributes[0];
    const def: CriteriaItem = {
      AttributeID: firstAttr?.ID || "",
      Operator:
        firstAttr &&
        (firstAttr.Type === "string" || firstAttr.Type === "list" || firstAttr.Type === "comments")
          ? "ILIKE"
          : "=",
      Value: "",
      Type: firstAttr?.Type || "string",
    };
    onChange({ criteria: [...criteria, def], logic });
  };

  const changeLogic = (value: "AND" | "OR") => onChange({ criteria: [...criteria], logic: value });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {criteria.map((c, idx) => {
        const selectedAttr = attributes.find((a) => a.ID === c.AttributeID);
        const isDate = selectedAttr?.Type === "date";
        const isNumber = selectedAttr?.Type === "number";
        const parsed = parseRelativeToken(c.Value || "");
        const months = parsed.relative ? parsed.months : 3;
        const sign = parsed.relative ? parsed.sign : -1;
        return (
          <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <DarkSelect
              placeholder="Attribut"
              value={c.AttributeID || null}
              onChange={(_, value) => {
                if (readOnly) return;
                const attr = attributes.find((a) => a.ID === value);
                updateAt(idx, {
                  AttributeID: value as string,
                  Operator:
                    attr &&
                    (attr.Type === "string" || attr.Type === "list" || attr.Type === "comments")
                      ? "ILIKE"
                      : "=",
                  Value: "",
                  Type: attr?.Type || "string",
                });
              }}
              size="sm"
              sx={{ minWidth: 180 }}
            >
              {attributes.map((a) => (
                <Option key={a.ID} value={a.ID}>
                  {a.Label}
                </Option>
              ))}
            </DarkSelect>

            <DarkSelect
              placeholder="Op"
              value={c.Operator || null}
              onChange={(_, value) => !readOnly && updateAt(idx, { Operator: value as string })}
              size="sm"
              sx={{ minWidth: 110 }}
            >
              {getOperatorOptions(selectedAttr?.Type || "string").map((op) => (
                <Option key={op.value} value={op.value}>
                  {op.label}
                </Option>
              ))}
            </DarkSelect>

            {selectedAttr && equipmentAttributeUsesEnumPicklist(selectedAttr) ? (
              <DarkSelect
                placeholder="Valeur"
                value={
                  equipmentPicklistSelectDisplayValue(c.Operator, c.Value ?? "") ??
                  (c.Value || null)
                }
                onChange={(_, value) => {
                  if (readOnly) return;
                  const raw = String(value ?? "");
                  const mapped = resolveEquipmentListFilterSentinel(raw);

                  if (mapped) {
                    updateAt(idx, { Value: mapped.value, Operator: mapped.operator });

                    return;
                  }

                  updateAt(idx, { Value: raw });
                }}
                size="sm"
                sx={{ minWidth: 180 }}
              >
                <Option value={EQUIPMENT_LIST_FILTER_EMPTY_SENTINEL}>Non renseigné</Option>
                {(selectedAttr.Options || []).map((o: AttributeOption) => (
                  <Option key={o.ID} value={o.Label}>
                    {o.Label}
                  </Option>
                ))}
              </DarkSelect>
            ) : isDate ? (
              parsed.relative ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <IconButtonCTA
                    ctaVariant={sign < 0 ? "accent" : "neutral"}
                    size="sm"
                    onClick={() =>
                      !readOnly &&
                      updateAt(idx, { Value: months === 0 ? "now" : `now-${Math.abs(months)}m` })
                    }
                    aria-label="Date relative négative"
                    title="Aujourd'hui - N mois"
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButtonCTA>
                  <IconButtonCTA
                    ctaVariant={sign > 0 ? "accent" : "neutral"}
                    size="sm"
                    onClick={() =>
                      !readOnly &&
                      updateAt(idx, { Value: months === 0 ? "now" : `now+${Math.abs(months)}m` })
                    }
                    aria-label="Date relative positive"
                    title="Aujourd'hui + N mois"
                  >
                    <AddCircleOutlineIcon />
                  </IconButtonCTA>
                  <NumericInput
                    mode="int"
                    value={String(months)}
                    onValueChange={(raw) => {
                      if (readOnly) return;
                      const n = Number(raw || 0);
                      const token =
                        n === 0 ? "now" : sign < 0 ? `now-${Math.abs(n)}m` : `now+${Math.abs(n)}m`;
                      updateAt(idx, { Value: token });
                    }}
                    size="sm"
                    sx={{ width: 90 }}
                    placeholder="Mois"
                  />
                  <DarkTypography
                    level="body-xs"
                    colorVariant="accent"
                    sx={{ fontWeight: 700, ml: 0.5 }}
                  >
                    Mois
                  </DarkTypography>
                  <Button
                    variant="solid"
                    color="neutral"
                    size="sm"
                    onClick={() => !readOnly && updateAt(idx, { Value: "" })}
                    sx={{ ml: 1 }}
                  >
                    Relatif
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <DarkInput
                    placeholder="YYYY-MM-DD"
                    value={c.Value || ""}
                    onChange={(e) => !readOnly && updateAt(idx, { Value: e.target.value })}
                    size="sm"
                    type="date"
                    sx={{ minWidth: 180 }}
                  />
                  <Button
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    onClick={() => !readOnly && updateAt(idx, { Value: "now-3m" })}
                  >
                    Relatif
                  </Button>
                </Box>
              )
            ) : isNumber ? (
              <NumericInput
                mode="decimal"
                allowNegative
                value={c.Value || ""}
                onValueChange={(raw) => {
                  if (readOnly) return;
                  updateAt(idx, { Value: raw });
                }}
                size="sm"
                sx={{ minWidth: 180 }}
                placeholder="Valeur"
              />
            ) : (
              <DarkInput
                placeholder="Valeur"
                value={c.Value || ""}
                onChange={(e) => !readOnly && updateAt(idx, { Value: e.target.value })}
                size="sm"
              />
            )}

            {!readOnly && (
              <IconButtonCTA
                ctaVariant="danger"
                size="sm"
                aria-label="Supprimer critère"
                onClick={() => removeAt(idx)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButtonCTA>
            )}
          </Box>
        );
      })}

      {!readOnly && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            variant="plain"
            size="sm"
            onClick={addNew}
            startDecorator={<AddCircleOutlineIcon />}
            sx={{
              color: colorTokens.status.success,
              "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
            }}
          >
            Ajouter un critère
          </Button>
          {criteria.length > 1 && (
            <>
              <DarkTypography level="body-xs" colorVariant="muted">
                Logique:
              </DarkTypography>
              <DarkSelect
                value={logic}
                onChange={(_, value) => changeLogic((value as "AND" | "OR") || "AND")}
                size="sm"
                sx={{ width: 100 }}
              >
                <Option value="AND">ET</Option>
                <Option value="OR">OU</Option>
              </DarkSelect>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
